import { useState } from "react";
import { Head, Link } from "zudoku/components";
import { ArrowRightIcon } from "zudoku/icons";
import { CATEGORIES, FLOW, PRODUCTS, QUICK_LINKS, ROLES } from "./content";

const ALL = "all";

const ProductCard = ({ product }: { product: (typeof PRODUCTS)[number] }) => (
  <Link to={product.to} className="fin-card" data-tone={product.tone}>
    <span className="fin-card__node" />
    <span className="fin-card__node" />
    <span className="fin-card__node" />
    <span className="fin-card__node" />
    <div>
      <div className="fin-card__top">
        <span>{CATEGORIES.find((c) => c.id === product.category)?.label}</span>
        <span
          className="fin-card__status"
          data-status={product.available ? "live" : "soon"}
        >
          {product.available ? "Live" : "Coming soon"}
        </span>
      </div>
      <h3 className="fin-card__title fin-deck">{product.name}</h3>
      <p className="fin-card__body">{product.summary}</p>
    </div>
    <div className="fin-card__bottom">
      <span className="fin-card__dots" aria-hidden />
      <span className="fin-card__btn" aria-hidden>
        <ArrowRightIcon size={18} />
      </span>
    </div>
  </Link>
);

export const LandingPage = () => {
  const [category, setCategory] = useState<string>(ALL);
  const products =
    category === ALL
      ? PRODUCTS
      : PRODUCTS.filter((product) => product.category === category);

  return (
    <div className="fin-landing">
      <Head>
        <title>Finarkein Docs</title>
      </Head>

      <section className="fin-frame fin-hero">
        <div className="fin-hero__title">
          <div className="fin-eyebrow fin-eyebrow--accent">Finarkein docs</div>
          <h1 className="fin-deck">
            Build on consented data, <mark>end to end</mark>.
          </h1>
          <p className="fin-hero__lede">
            Guides, concepts and API references for the Finarkein Platform:
            data collection, intelligence, decisioning and embedded finance
            for banks, lenders, insurers and wealth platforms.
          </p>
          <div className="fin-hero__actions">
            <a href="#platform" className="fin-btn fin-btn--primary">
              Explore the platform <ArrowRightIcon size={16} />
            </a>
            <a href="#roles" className="fin-btn fin-btn--ghost">
              Find your role
            </a>
          </div>
        </div>
        <div className="fin-hero__side">
          <div className="fin-glyph-cell" aria-hidden>
            <span className="fin-glyph-cell__arrows fin-deck">→→</span>
          </div>
          <nav className="fin-quick" aria-label="Popular pages">
            <div className="fin-eyebrow">Popular</div>
            <ul>
              {QUICK_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>
                    {link.label}
                    <ArrowRightIcon size={14} />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      <section id="platform" className="fin-frame fin-section">
        <div className="fin-eyebrow">Finarkein Platform</div>
        <div className="fin-section__head">
          <h2 className="fin-deck">From consent to action, one platform.</h2>
          <p>
            Every product is part of the Finarkein Platform. Use one on its
            own, or combine them to take customer data through to an action.{" "}
            <Link to="/platform">Platform overview</Link>
          </p>
        </div>
        <div className="fin-tabs" role="tablist" aria-label="Platform layer">
          {[
            { id: ALL, label: "All" },
            ...CATEGORIES.filter((c) => PRODUCTS.some((p) => p.category === c.id)),
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={category === tab.id}
              onClick={() => setCategory(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="fin-cards">
          {products.map((product) => (
            <ProductCard key={product.name} product={product} />
          ))}
        </div>
      </section>

      <section id="roles" className="fin-frame fin-section">
        <div className="fin-eyebrow">Your way in</div>
        <div className="fin-section__head">
          <h2 className="fin-deck">Start from what you do.</h2>
          <p>
            Pick your role for a reading path across the products that apply
            to it.
          </p>
        </div>
        <div className="fin-roles">
          {ROLES.map(({ title, to, summary, Icon, links }) => (
            <div key={title} className="fin-role">
              <span className="fin-role__icon">
                <Icon size={20} strokeWidth={1.75} />
              </span>
              <h3 className="fin-deck">
                <Link to={to}>{title}</Link>
              </h3>
              <p>{summary}</p>
              <ul>
                {links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to}>
                      {link.label} <ArrowRightIcon size={12} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="fin-frame fin-section">
        <div className="fin-eyebrow">How it fits together</div>
        <div className="fin-section__head">
          <h2 className="fin-deck">Four layers, one data trail.</h2>
          <p>
            Each layer builds on the one before it. Consent travels with the
            data the whole way.
          </p>
        </div>
        <ol className="fin-flow" style={{ listStyle: "none", padding: 0 }}>
          {FLOW.map((step, index) => (
            <li key={step.title} className="fin-flow__step">
              <span className="fin-flow__num">0{index + 1}</span>
              <h3 className="fin-deck">{step.title}</h3>
              <p>{step.body}</p>
              <small>{step.products}</small>
            </li>
          ))}
        </ol>
      </section>

      <section className="fin-frame fin-band">
        <div>
          <h2 className="fin-deck">Need credentials or a sandbox?</h2>
          <p>Access is set up at onboarding, per product.</p>
        </div>
        <Link to="/resources/getting-access" className="fin-btn fin-btn--primary">
          Getting access <ArrowRightIcon size={16} />
        </Link>
      </section>
    </div>
  );
};
